import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  Chip,
  Switch,
  FormControlLabel,
  Divider,
  Paper
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Payment as PaymentIcon,
  Security as SecurityIcon,
  ContactPhone as ContactIcon
} from '@mui/icons-material';

interface AddEmployeeDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (employeeData: any) => Promise<void>;
}

const AddEmployeeDialog: React.FC<AddEmployeeDialogProps> = ({ open, onClose, onSubmit }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [employeeData, setEmployeeData] = useState({
    // Personal Information
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',

    // Address
    address_line_1: '',
    address_line_2: '',
    city: '',
    county: '',
    postcode: '',

    // Employment Details
    employee_number: '',
    department: '',
    job_title: '',
    work_location: 'office',
    employee_type: 'permanent',
    manager_id: '',
    start_date: '',
    probation_end_date: '',

    // Salary & Benefits
    annual_salary: '',
    hourly_rate: '',
    payroll_frequency: 'monthly',

    // US Tax Information
    social_security_number: '',
    tax_code: 'W4-STD',
    student_loan: false,
    pension_scheme: true,

    // Bank Details
    bank_name: '',
    account_holder_name: '',
    routing_number: '',
    account_number: '',

    // Emergency Contact
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relationship: ''
  });

  const [errors, setErrors] = useState<any>({});

  const handleInputChange = (field: string, value: any) => {
    setEmployeeData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev: any) => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = (step: number) => {
    const newErrors: any = {};

    switch (step) {
      case 0: // Personal Information
        if (!employeeData.first_name) newErrors.first_name = 'First name is required';
        if (!employeeData.last_name) newErrors.last_name = 'Last name is required';
        if (!employeeData.email) newErrors.email = 'Email is required';
        if (employeeData.email && !isValidEmail(employeeData.email)) {
          newErrors.email = 'Please enter a valid email address';
        }
        if (employeeData.postcode && !isValidUSZipCode(employeeData.postcode)) {
          newErrors.postcode = 'Please enter a valid US ZIP code';
        }
        break;

      case 1: // Employment Details
        if (!employeeData.employee_number) newErrors.employee_number = 'Employee number is required';
        if (!employeeData.department) newErrors.department = 'Department is required';
        if (!employeeData.job_title) newErrors.job_title = 'Job title is required';
        if (!employeeData.start_date) newErrors.start_date = 'Start date is required';
        break;

      case 2: // Salary & Tax
        if (!employeeData.annual_salary && !employeeData.hourly_rate) {
          newErrors.salary = 'Either annual salary or hourly rate is required';
        }
        if (employeeData.social_security_number && !isValidNINumber(employeeData.social_security_number)) {
          newErrors.social_security_number = 'Invalid format. Use: XXX-XX-XXXX. Example: 123-45-6789';
        }
        break;

      case 3: // Bank Details
        if (employeeData.routing_number && !isValidSortCode(employeeData.routing_number)) {
          newErrors.routing_number = 'Please enter a valid routing number (XX-XX-XX)';
        }
        if (employeeData.account_number && !isValidAccountNumber(employeeData.account_number)) {
          newErrors.account_number = 'Please enter a valid account number (8 digits)';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = () => {
    if (validateStep(activeStep)) {
      // Generate employee number if not provided
      if (!employeeData.employee_number) {
        employeeData.employee_number = `EMP${Date.now().toString().slice(-6)}`;
      }

      onSubmit(employeeData);
      handleClose();
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setEmployeeData({
      first_name: '', last_name: '', email: '', phone: '', date_of_birth: '',
      address_line_1: '', address_line_2: '', city: '', county: '', postcode: '',
      employee_number: '', department: '', job_title: '', work_location: 'office',
      employee_type: 'permanent', manager_id: '', start_date: '', probation_end_date: '',
      annual_salary: '', hourly_rate: '', payroll_frequency: 'monthly',
      social_security_number: '', tax_code: 'W4-STD', student_loan: false, pension_scheme: true,
      bank_name: '', account_holder_name: '', routing_number: '', account_number: '',
      emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relationship: ''
    });
    setErrors({});
    onClose();
  };

  const steps = [
    {
      label: 'Personal Information',
      icon: <PersonIcon />,
      content: (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="First Name *"
              value={employeeData.first_name}
              onChange={(e) => handleInputChange('first_name', e.target.value)}
              error={!!errors.first_name}
              helperText={errors.first_name}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Last Name *"
              value={employeeData.last_name}
              onChange={(e) => handleInputChange('last_name', e.target.value)}
              error={!!errors.last_name}
              helperText={errors.last_name}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Email Address *"
              type="email"
              value={employeeData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Phone Number"
              value={employeeData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              placeholder="(555) 123-4567"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Date of Birth"
              type="date"
              value={employeeData.date_of_birth}
              onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Address</Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address Line 1"
              value={employeeData.address_line_1}
              onChange={(e) => handleInputChange('address_line_1', e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address Line 2"
              value={employeeData.address_line_2}
              onChange={(e) => handleInputChange('address_line_2', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="City"
              value={employeeData.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="County"
              value={employeeData.county}
              onChange={(e) => handleInputChange('county', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Postcode"
              value={employeeData.postcode}
              onChange={(e) => handleInputChange('postcode', e.target.value.toUpperCase())}
              error={!!errors.postcode}
              helperText={errors.postcode}
              placeholder="SW1A 1AA"
            />
          </Grid>
        </Grid>
      )
    },
    {
      label: 'Employment Details',
      icon: <BusinessIcon />,
      content: (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Employee Number *"
              value={employeeData.employee_number}
              onChange={(e) => handleInputChange('employee_number', e.target.value)}
              error={!!errors.employee_number}
              helperText={errors.employee_number || 'Auto-generated if left blank'}
              placeholder="EMP001"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.department}>
              <InputLabel>Department *</InputLabel>
              <Select
                value={employeeData.department}
                onChange={(e) => handleInputChange('department', e.target.value)}
              >
                <MenuItem value="Sales">Sales</MenuItem>
                <MenuItem value="Marketing">Marketing</MenuItem>
                <MenuItem value="IT">IT</MenuItem>
                <MenuItem value="Finance">Finance</MenuItem>
                <MenuItem value="HR">HR</MenuItem>
                <MenuItem value="Operations">Operations</MenuItem>
                <MenuItem value="Warehouse">Warehouse</MenuItem>
                <MenuItem value="Cleaning">Cleaning</MenuItem>
                <MenuItem value="Management">Management</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Job Title *"
              value={employeeData.job_title}
              onChange={(e) => handleInputChange('job_title', e.target.value)}
              error={!!errors.job_title}
              helperText={errors.job_title}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Work Location</InputLabel>
              <Select
                value={employeeData.work_location}
                onChange={(e) => handleInputChange('work_location', e.target.value)}
              >
                <MenuItem value="office">Office</MenuItem>
                <MenuItem value="remote">Remote</MenuItem>
                <MenuItem value="hybrid">Hybrid</MenuItem>
                <MenuItem value="warehouse">Warehouse</MenuItem>
                <MenuItem value="field">Field</MenuItem>
                <MenuItem value="client_site">Client Site</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Employment Type</InputLabel>
              <Select
                value={employeeData.employee_type}
                onChange={(e) => handleInputChange('employee_type', e.target.value)}
              >
                <MenuItem value="permanent">Permanent</MenuItem>
                <MenuItem value="contract">Contract</MenuItem>
                <MenuItem value="temporary">Temporary</MenuItem>
                <MenuItem value="apprentice">Apprentice</MenuItem>
                <MenuItem value="intern">Intern</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Start Date *"
              type="date"
              value={employeeData.start_date}
              onChange={(e) => handleInputChange('start_date', e.target.value)}
              error={!!errors.start_date}
              helperText={errors.start_date}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Probation End Date"
              type="date"
              value={employeeData.probation_end_date}
              onChange={(e) => handleInputChange('probation_end_date', e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Usually 3-6 months from start date"
            />
          </Grid>
        </Grid>
      )
    },
    {
      label: 'Salary & Benefits',
      icon: <PaymentIcon />,
      content: (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>� US Salary Configuration</Typography>
              <Typography variant="body2">
                Configure salary or hourly rate. The system will automatically calculate federal/state tax,
                Social Security, Medicare, and 401(k) contributions based on current US rates.
              </Typography>
            </Alert>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Annual Salary ($)"
              type="number"
              value={employeeData.annual_salary}
              onChange={(e) => handleInputChange('annual_salary', e.target.value)}
              error={!!errors.salary}
              helperText={errors.salary || 'Leave blank if using hourly rate'}
              placeholder="45000"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Hourly Rate ($)"
              type="number"
              value={employeeData.hourly_rate}
              onChange={(e) => handleInputChange('hourly_rate', e.target.value)}
              error={!!errors.salary}
              helperText="Leave blank if using annual salary"
              placeholder="15.50"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Payroll Frequency</InputLabel>
              <Select
                value={employeeData.payroll_frequency}
                onChange={(e) => handleInputChange('payroll_frequency', e.target.value)}
              >
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="fortnightly">Fortnightly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>US Benefits & Deductions</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={employeeData.pension_scheme}
                  onChange={(e) => handleInputChange('pension_scheme', e.target.checked)}
                />
              }
              label="Auto-enroll in 401(k) Plan"
            />
            <Typography variant="caption" display="block" color="text.secondary">
              Optional employer-sponsored retirement plan with employer match
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={employeeData.student_loan}
                  onChange={(e) => handleInputChange('student_loan', e.target.checked)}
                />
              }
              label="Student Loan Deductions"
            />
            <Typography variant="caption" display="block" color="text.secondary">
              Automatic deductions will be calculated based on salary
            </Typography>
          </Grid>
        </Grid>
      )
    },
    {
      label: 'Tax & Social Security',
      icon: <SecurityIcon />,
      content: (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>�🇸 US Tax Information Required</Typography>
              <Typography variant="body2">
                This information is required for IRS compliance and payroll processing.
                All data is encrypted and stored securely.
              </Typography>
            </Alert>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Social Security Number"
              value={employeeData.social_security_number}
              onChange={(e) => handleInputChange('social_security_number', e.target.value.toUpperCase())}
              error={!!errors.social_security_number}
              helperText={errors.social_security_number || 'Format: XXX-XX-XXXX. Example: 123-45-6789'}
              placeholder="123-45-6789"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Tax Code</InputLabel>
              <Select
                value={employeeData.tax_code}
                onChange={(e) => handleInputChange('tax_code', e.target.value)}
              >
                <MenuItem value="W4-STD">W-4 Standard - Single or Married Filing Separately</MenuItem>
                <MenuItem value="W4-MFJ">W-4 MFJ - Married Filing Jointly</MenuItem>
                <MenuItem value="W4-HOH">W-4 HOH - Head of Household</MenuItem>
                <MenuItem value="W4-EXM">W-4 Exempt - No withholding</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>Bank Details for Payroll</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Bank Name"
              value={employeeData.bank_name}
              onChange={(e) => handleInputChange('bank_name', e.target.value)}
              placeholder="Chase"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Account Holder Name"
              value={employeeData.account_holder_name}
              onChange={(e) => handleInputChange('account_holder_name', e.target.value)}
              placeholder="John Smith"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Routing Number"
              value={employeeData.routing_number}
              onChange={(e) => handleInputChange('routing_number', e.target.value)}
              error={!!errors.routing_number}
              helperText={errors.routing_number || 'Format: 9 digits'}
              placeholder="021000021"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Account Number"
              value={employeeData.account_number}
              onChange={(e) => handleInputChange('account_number', e.target.value)}
              error={!!errors.account_number}
              helperText={errors.account_number || '8 digits'}
              placeholder="12345678"
            />
          </Grid>
        </Grid>
      )
    },
    {
      label: 'Emergency Contact',
      icon: <ContactIcon />,
      content: (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Emergency Contact Information</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              This information will only be used in case of emergency and is stored securely.
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Emergency Contact Name"
              value={employeeData.emergency_contact_name}
              onChange={(e) => handleInputChange('emergency_contact_name', e.target.value)}
              placeholder="Jane Smith"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Emergency Contact Phone"
              value={employeeData.emergency_contact_phone}
              onChange={(e) => handleInputChange('emergency_contact_phone', e.target.value)}
              placeholder="(555) 123-4567"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Relationship</InputLabel>
              <Select
                value={employeeData.emergency_contact_relationship}
                onChange={(e) => handleInputChange('emergency_contact_relationship', e.target.value)}
              >
                <MenuItem value="spouse">Spouse</MenuItem>
                <MenuItem value="partner">Partner</MenuItem>
                <MenuItem value="parent">Parent</MenuItem>
                <MenuItem value="sibling">Sibling</MenuItem>
                <MenuItem value="child">Child</MenuItem>
                <MenuItem value="friend">Friend</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>✅ Ready to Create Employee</Typography>
              <Typography variant="body2">
                All required information has been collected. The employee will be created with:
                <br />• US-compliant tax and FICA setup
                <br />• Automatic 401(k) enrollment (if eligible)
                <br />• Payroll-ready configuration
                <br />• IRS compliance
              </Typography>
            </Alert>
          </Grid>
        </Grid>
      )
    }
  ];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon color="primary" />
          Add New Employee
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel icon={step.icon}>
                  <Typography variant="h6">{step.label}</Typography>
                </StepLabel>
                <StepContent>
                  <Box sx={{ mt: 2, mb: 2 }}>
                    {step.content}
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose}>Cancel</Button>
        {activeStep > 0 && (
          <Button onClick={handleBack}>Back</Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button variant="contained" onClick={handleNext}>
            Next
          </Button>
        ) : (
          <Button variant="contained" onClick={handleSubmit}>
            Create Employee
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// Validation functions
const isValidEmail = (email: string) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const isValidUSZipCode = (postcode: string) => {
  return /^\d{5}(-\d{4})?$/.test(postcode.trim());
};

const isValidNINumber = (ni: string) => {
  return /^\d{3}-?\d{2}-?\d{4}$/.test(ni.replace(/\s/g, ''));
};

const isValidSortCode = (routingNumber: string) => {
  return /^\d{9}$/.test(routingNumber);
};

const isValidAccountNumber = (accountNumber: string) => {
  return /^\d{8}$/.test(accountNumber);
};

export default AddEmployeeDialog;
